import React from "react";
import html2pdf from "html2pdf.js";
import defaultLogo from "../../../images/ensenartecorto.png";

/**
 * Botón que genera la Orden de Pago en PDF replicando el diseño de referencia:
 * cabecera institucional + N° de orden, tabla de datos del estudiante,
 * tabla de conceptos a pagar, bloque de cuentas + datos bancarios, resumen
 * de descuento/total, línea de "corte aquí" y 3 recibos/talonarios repetidos.
 *
 * El PDF SIEMPRE debe quedar en una sola hoja: por eso el diseño se mantiene
 * compacto (igual que la referencia, que deja espacio en blanco debajo).
 *
 * Props esperadas (las que ya envía Hoja.js):
 *  - orderData: objeto devuelto por buildOrderData() en Hoja.js
 *  - logo: import de la imagen del logo (opcional, si no se pasa usa el default)
 *  - onStart / onDone: callbacks para manejar el estado "Generando..."
 *  - disabled: boolean
 *  - archivoNombre: nombre base del archivo a descargar
 */
function GenerarPdfButton({
  orderData,
  logo,
  onStart,
  onDone,
  disabled,
  archivoNombre,
}) {
  // Formatea a "$1.650.000" (estilo colombiano, sin decimales)
  const formatCurrency = (value) => {
    const n = Math.round(Number(value) || 0);
    const formatted = Math.abs(n)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${n < 0 ? "-" : ""}$${formatted}`;
  };

  const getBase64Image = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleGenerar = async () => {
    if (!orderData) return;

    onStart && onStart();

    try {
      const logoUrl = logo || defaultLogo;
      const logo64 = await getBase64Image(logoUrl);

      const {
        institucion = {},
        orden = {},
        estudiante = {},
        pagos = [],
        cuentas = [],
        datos_bancarios = {},
        descuento = {},
        total_a_pagar = 0,
        fecha_limite = "",
      } = orderData;

      const subtotal = pagos.reduce(
        (acc, p) => acc + (Number(p.valor) || 0),
        0
      );

      const fileName =
        archivoNombre ||
        `orden_de_pago_${(estudiante.nombre || "estudiante").replace(
          /\s+/g,
          "_"
        )}`;

      const pagosRows = pagos.length
        ? pagos
            .map(
              (p) => `
          <tr>
            <td class="cell">${p.concepto || "Pago"}</td>
            <td class="cell">${p.fecha || ""}</td>
            <td class="cell cell-right">${formatCurrency(p.valor)}</td>
          </tr>`
            )
            .join("")
        : `<tr><td colspan="3" class="cell cell-center">No hay pagos registrados</td></tr>`;

      const cuentasRows = cuentas
        .map(
          (c) => `
        <div class="cuenta-row">
          <span>${c.etiqueta}</span>
          <span>${formatCurrency(c.valor)}</span>
        </div>`
        )
        .join("");

      const buildTicket = () => `
        <div class="ticket">
          <img src="${logo64}" class="ticket-logo" />
          <div class="ticket-info">
            <div><span class="bold">Estudiante:</span> ${
              estudiante.nombre || ""
            }</div>
            <div><span class="bold">Identificación:</span> Cc:${
              estudiante.identificacion || ""
            } &middot; <span class="bold">Programa:</span> ${
              estudiante.programa || ""
            }</div>
          </div>
          <div class="ticket-fecha">
            <div class="ticket-label">HASTA</div>
            <div class="ticket-fecha-valor">${fecha_limite || ""}</div>
          </div>
          <div class="ticket-total">
            <div class="ticket-label">TOTAL A PAGAR</div>
            <div class="ticket-total-valor">${formatCurrency(
              total_a_pagar
            )}</div>
          </div>
        </div>`;

      const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8" />
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #1f2937;
    margin: 0;
    padding: 22px 26px;
    font-size: 12px;
  }

  .header { display: flex; align-items: flex-start; justify-content: space-between; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo { width: 54px; }
  .inst-nombre { font-size: 14.5px; font-weight: bold; color: #111827; }
  .inst-detalle { font-size: 9.5px; color: #6b7280; margin-top: 2px; }
  .orden-box { text-align: right; }
  .orden-label { font-size: 10px; color: #6b7280; letter-spacing: 1px; }
  .orden-numero { font-size: 18px; font-weight: bold; color: #57bee1; }

  .titulo-wrap { text-align: center; margin: 16px 0 18px; }
  .titulo {
    font-size: 19px;
    font-weight: bold;
    letter-spacing: 3px;
    color: #111827;
    display: inline-block;
    padding-bottom: 7px;
    border-bottom: 3px solid #57bee1;
  }

  table.info-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 11.5px; }
  table.info-table td { border: 1px solid #d1d5db; padding: 7px 10px; }
  table.info-table td.label { font-weight: bold; width: 130px; background: #f9fafb; }

  table.pagos-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 11px; }
  table.pagos-table thead th {
    background: #1e293b; color: #ffffff; text-align: left;
    padding: 8px 10px; font-size: 10.5px; letter-spacing: 0.5px;
  }
  table.pagos-table th.th-right, table.pagos-table td.cell-right { text-align: right; }
  table.pagos-table td.cell { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; }
  table.pagos-table td.cell-center { text-align: center; color: #9ca3af; padding: 12px; }

  .cuentas-bancarias { display: flex; gap: 12px; margin-bottom: 12px; }
  .cuentas-box, .bancarios-box {
    flex: 1; background: #eef3f8; border-radius: 8px; padding: 10px 14px; font-size: 11px;
  }
  .cuenta-row { display: flex; justify-content: space-between; padding: 2px 0; font-weight: bold; }
  .bancarios-box .linea { margin-bottom: 3px; }
  .bold { font-weight: bold; }

  .resumen-row { display: flex; gap: 10px; margin-bottom: 12px; }
  .resumen-box {
    border: 1px solid #d1d5db; border-radius: 6px; padding: 10px 14px;
    display: flex; align-items: center; font-size: 12px;
  }
  .resumen-descuento { font-weight: bold; color: #57bee1; }
  .resumen-subtotal { flex: 1; justify-content: space-between; }
  .resumen-subtotal-label { color: #374151; font-weight: bold; }
  .resumen-total {
    background: #1e293b; color: #ffffff; flex-direction: column;
    align-items: flex-end; justify-content: center; min-width: 150px;
  }
  .resumen-total-label { font-size: 10px; letter-spacing: 1px; color: #cbd5e1; }
  .resumen-total-valor { font-size: 18px; font-weight: bold; color: #57bee1; }

  .corte {
    text-align: center; color: #9ca3af; font-size: 10px;
    border-top: 1px dashed #d1d5db; padding-top: 8px; margin: 10px 0 14px;
  }

  .ticket {
    display: flex; align-items: center; gap: 12px;
    border: 1px solid #e5e7eb; border-left: 4px solid #0ca3b5; border-radius: 6px;
    padding: 8px 12px; margin-bottom: 8px; font-size: 10.5px;
  }
  .ticket-logo { width: 24px; }
  .ticket-info { flex: 1; }
  .ticket-fecha, .ticket-total { text-align: center; min-width: 90px; }
  .ticket-label { font-size: 8.5px; color: #9ca3af; letter-spacing: 0.5px; }
  .ticket-fecha-valor { font-weight: bold; }
  .ticket-total-valor { font-weight: bold; color: #57bee1; font-size: 13px; }

  .footer { text-align: right; font-size: 10px; color: #9ca3af; margin-top: 6px; }
</style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <img src="${logo64}" class="logo" />
      <div>
        <div class="inst-nombre">${institucion.nombre || ""}</div>
        <div class="inst-detalle">${institucion.resolucion || ""}</div>
        <div class="inst-detalle">${institucion.direccion || ""} &middot; Tel: ${
        institucion.telefonos || ""
      } &middot; Nit: ${institucion.nit || ""}</div>
      </div>
    </div>
    <div class="orden-box">
      <div class="orden-label">ORDEN N&deg;</div>
      <div class="orden-numero">${orden.numero || ""}</div>
    </div>
  </div>

  <div class="titulo-wrap">
    <span class="titulo">ORDEN DE PAGO</span>
  </div>

  <table class="info-table">
    <tr>
      <td class="label">Estudiante:</td>
      <td colspan="3">${estudiante.nombre || ""}</td>
    </tr>
    <tr>
      <td class="label">Identificación:</td>
      <td>Cc:${estudiante.identificacion || ""}</td>
      <td class="label">Período:</td>
      <td>${estudiante.periodo || ""}</td>
    </tr>
    <tr>
      <td class="label">Programa:</td>
      <td colspan="3">${estudiante.programa || ""}</td>
    </tr>
  </table>

  <table class="pagos-table">
    <thead>
      <tr>
        <th>DTO/CONCEPTO</th>
        <th>FECHA</th>
        <th class="th-right">VALOR A PAGAR</th>
      </tr>
    </thead>
    <tbody>
      ${pagosRows}
    </tbody>
  </table>

  <div class="cuentas-bancarias">
    <div class="cuentas-box">
      ${cuentasRows}
    </div>
    <div class="bancarios-box">
      <div class="linea"><span class="bold">${
        datos_bancarios.titular || ""
      }:</span> ${datos_bancarios.descripcion || ""}</div>
      <div class="linea"><span class="bold">${
        datos_bancarios.banco || ""
      }</span> ${datos_bancarios.numero_cuenta || ""} &middot; llave ${
        datos_bancarios.llave || ""
      }</div>
    </div>
  </div>

  <div class="resumen-row">
    <div class="resumen-box resumen-descuento">${
      descuento.porcentaje || "SIN DESCUENTO"
    }</div>
    <div class="resumen-box resumen-subtotal">
      <span class="resumen-subtotal-label">TOTALIDAD DEL PROGRAMA</span>
      <span>${formatCurrency(subtotal)}</span>
    </div>
    <div class="resumen-box resumen-total">
      <div class="resumen-total-label">TOTAL A PAGAR</div>
      <div class="resumen-total-valor">${formatCurrency(total_a_pagar)}</div>
    </div>
  </div>

  <div class="corte">&#9986; corte aquí</div>

  ${buildTicket()}
  ${buildTicket()}
  ${buildTicket()}

  <div class="footer">Fecha de impresión: ${orden.fecha_impresion || ""}</div>
</body>
</html>`;

      const element = document.createElement("div");
      element.innerHTML = html;

      const options = {
        margin: 8,
        filename: `${fileName}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        // El diseño es compacto a propósito para que SIEMPRE quepa en una sola hoja
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      await html2pdf().set(options).from(element).save();
    } catch (err) {
      console.error("Error generando PDF:", err);
    } finally {
      onDone && onDone();
    }
  };

  return (
    <button className="btn-primary" onClick={handleGenerar} disabled={disabled}>
      {disabled ? "Generando..." : "Generar PDF"}
    </button>
  );
}

export default GenerarPdfButton;